'use client';

interface GlowingBorderProps {
  backgroundColor: string;
  width?: number | '100%';
  height?: number | '100%';
  borderRadius?: number;
}

const LIGHTS_LIST = [
  { start: 0, end: 100 },
  { start: 50, end: 150 },
];

export default function GlowingBorder({
  backgroundColor,
  width = '100%',
  height = '100%',
  borderRadius = 4,
}: GlowingBorderProps) {
  const widthStyle = width === '100%' ? 'calc(100% + 3px)' : `${width}px`;
  const heightStyle = height === '100%' ? 'calc(100% + 3px)' : `${height}px`;

  return (
    <>
      <style>{`
        ${LIGHTS_LIST.map(
          ({ start, end }) => `
          @keyframes glowMove-${start}-${end} {
            0% { offset-distance: ${start}%; }
            100% { offset-distance: ${end}%; }
          }
        `,
        ).join('')}
      `}</style>

      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden"
        style={{
          width: widthStyle,
          height: heightStyle,
          borderRadius: `${borderRadius}px`,
        }}
      >
        {/* Border path SVG */}
        <svg className="pointer-events-none absolute top-0 left-0 h-full w-full fill-none">
          <rect
            id="borderPath"
            width="100%"
            height="100%"
            rx={borderRadius}
            ry={borderRadius}
            stroke="none"
            fill="none"
          />
        </svg>

        {/* Glowing light container */}
        <div className="absolute top-1/2 left-1/2 z-1 h-full w-full -translate-x-1/2 -translate-y-1/2">
          {LIGHTS_LIST.map(({ start, end }) => (
            <div key={start}>
              {/* Inner light (white) */}
              <div
                className="absolute top-0 left-0 rounded-full"
                style={{
                  width: 50,
                  height: 50,
                  background: 'radial-gradient(circle at center, white 0%, transparent 80%)',
                  opacity: 0.8,
                  filter: 'blur(0px)',
                  offsetPath: 'url(#borderPath)',
                  offsetRotate: '0deg',
                  animation: `glowMove-${start}-${end} 5s linear infinite`,
                }}
              />
              {/* Outer light (brand color) */}
              <div
                className="absolute top-0 left-0 rounded-full"
                style={{
                  width: 100,
                  height: 100,
                  background: 'radial-gradient(circle at center, var(--color-glowing-border-glow) 0%, transparent 80%)',
                  opacity: 0.7,
                  filter: 'blur(2px)',
                  offsetPath: 'url(#borderPath)',
                  offsetRotate: '0deg',
                  animation: `glowMove-${start}-${end} 5s linear infinite`,
                }}
              />
            </div>
          ))}
        </div>

        {/* Background overlay */}
        <div
          className="absolute top-1/2 left-1/2 z-1 -translate-x-1/2 -translate-y-1/2"
          style={{
            width: 'calc(100% - 4px)',
            height: 'calc(100% - 3px)',
            backgroundColor,
            borderRadius: `calc(${borderRadius}px - 1px)`,
          }}
        />
      </div>
    </>
  );
}
